import { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { Container } from './styles';
import { useSudoku } from '../../../SudokuContext';
import { acceptsPartial, isCompleteValue } from '../../../shape';

interface SquareProps {
  x: number;
  y: number;
  values: string[];
  isHint: boolean;
  isRevealed?: boolean;
  value: string;
  boxWidth: number;
  boxHeight: number;
  isSquareSolution: string;
}

export const Square = ({
  x,
  y,
  values,
  isHint,
  isRevealed = false,
  boxHeight,
  boxWidth,
  value,
  isSquareSolution,
}: SquareProps) => {
  const {
    checkFocus,
    setValuesSquares,
    verifyFinish,
    width,
    height,
    isSudokuFinished,
    matrix,
  } = useSudoku();

  // x = linha, y = coluna: a divisória de linha fecha um bloco de boxHeight linhas
  const isRowDivider = (x + 1) % boxHeight === 0;
  const isColumnDivider = (y + 1) % boxWidth === 0;
  const maxLength = Math.max(...values.map((v) => v.length));

  // Texto que está presente no quadrado
  const [textInSquare, setTextInSquare] = useState<string>('');
  // Buffer para armazenamento temporário
  const [buffer, setBuffer] = useState<string>('');

  // Função ativada quando o quadrado é focado.
  const handleFocus = () => {
    checkFocus(x, y);
    setBuffer(textInSquare);
    setTextInSquare('');
  };

  // Função ativada quando o quadrado é desfocado.
  const handleBlur = useCallback(() => {
    // Prefixo que não é um valor completo é descartado (tratado como vazio)
    const finalText =
      textInSquare !== '' && !isCompleteValue(textInSquare, values)
        ? ''
        : textInSquare;
    if (finalText !== textInSquare) setTextInSquare('');

    if (finalText === '' && buffer === '' && matrix[x][y].value !== '') {
      setValuesSquares({
        x,
        y,
        newValue: '',
      });
    } else if (finalText !== '' && matrix[x][y].value !== finalText) {
      setValuesSquares({
        x,
        y,
        newValue: finalText,
      });
    } else if (
      finalText === '' &&
      buffer !== '' &&
      matrix[x][y].value === buffer
    ) {
      setTextInSquare(buffer);
    }

    verifyFinish();
  }, [
    buffer,
    setValuesSquares,
    textInSquare,
    values,
    verifyFinish,
    x,
    y,
    matrix,
  ]);

  // Função que verifica a entrada no input
  // Se for uma letra permitida ele salva no State
  const verifiedInput = (inputValue: string) => {
    if (acceptsPartial(inputValue, values)) {
      setTextInSquare(inputValue);
    }
  };
  // Função que verifica se o usuário deu Delete ou Backspace
  const keyCheck = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      setBuffer('');
    }
  };

  /* Estados iniciais para montar o Sudoku */
  useEffect(() => {
    if (isHint) {
      setTextInSquare(isSquareSolution);
    } else {
      value ? setTextInSquare(value) : setTextInSquare('');
    }
  }, [isHint, isSquareSolution, value]);

  return (
    <Container
      isFirstColumn={y === 0 ? true : false}
      isFirstRow={x === 0 ? true : false}
      isRowDivider={isRowDivider}
      isColumnDivider={isColumnDivider}
      data-row-divider={isRowDivider}
      data-column-divider={isColumnDivider}
      isCorrect={isSquareSolution === textInSquare ? true : false}
      itsCorrected={isSudokuFinished}
      isHint={isHint}
      isRevealed={isRevealed}
      data-revealed={isRevealed}
      width={width}
      height={height}
    >
      <input
        maxLength={maxLength}
        type="text"
        disabled={isHint || isRevealed || isSudokuFinished}
        value={textInSquare}
        onChange={(event) => verifiedInput(event.target.value)}
        onFocus={() => handleFocus()}
        onBlur={() => handleBlur()}
        onKeyDown={(event) => keyCheck(event)}
      />
    </Container>
  );
};
